import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { safeGet, safeSet, safeRemove, safeJsonParse, getSafeSession } from '../utils/browserSafe';
import baseURL from '../url';
import moneda from '../moneda';
import './CustomerAccount.css';
import { COUNTRY_PREFIXES, normalizePhone } from '../utils/countryPrefixes';

const emptyRegister = {
    nombreCompleto: '',
    direccion: '',
    telefono: '',
    email: '',
    password: '',
    passwordConfirm: '',
};

const estadoLabel = {
    pendiente: 'Pendiente',
    procesado: 'Procesado',
    entregado: 'Entregado',
    cancelado: 'Cancelado',
    eliminado: 'Eliminado',
};

function formatMoney(value) {
    return `${moneda} ${Number(value || 0).toFixed(2)}`;
}

function PedidoCard({ pedido, onReorder, onHide }) {
    const estado = String(pedido.estadoPedido || 'pendiente').toLowerCase();

    return (
        <div className='historyProductCard'>
            <div className='historyCardTop'>
                <div>
                    <strong>Factura #KY{pedido.facturaNumero || pedido.numeroOrden}</strong>
                    <span>{pedido.createdAt}</span>
                </div>
                <span className={`orderStatus ${estado}`}>{estadoLabel[estado] || estado}</span>
            </div>

            <div className='historyCardInfo'>
                <p><b>Total:</b> {formatMoney(pedido.total)}</p>
                <p><b>Entrega:</b> {pedido.tipoEntrega || '-'}</p>
                <p><b>Pago:</b> {pedido.formaPago || '-'}</p>
                {pedido.municipio ? <p><b>Municipio:</b> {pedido.municipio}</p> : null}
                {pedido.costoEnvio ? <p><b>Envío:</b> {formatMoney(pedido.costoEnvio)}</p> : null}
                {pedido.direccion ? <p className='wide'><b>Dirección:</b> {pedido.direccion}</p> : null}
                {pedido.detalles ? <p className='wide'><b>Detalles:</b> {pedido.detalles}</p> : null}
            </div>

            {pedido.items?.length ? (
                <div className='historyProductsGrid'>
                    {pedido.items.map((item, index) => (
                        <div className='historyItemCard' key={index}>
                            {item.imagen ? <img src={item.imagen} alt={item.titulo || 'Producto'} /> : <div className='historyNoImg'>KY</div>}
                            <div>
                                <strong>{item.titulo || 'Producto'}</strong>
                                <span>{Number(item.cantidad || 1)} unidad(es)</span>
                                {item.descripcion ? <small>{item.descripcion}</small> : null}
                                <b>{formatMoney(item.subtotalCobrado || (Number(item.precioCobrado || 0) * Number(item.cantidad || 1)))}</b>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <p className='emptyOrders'>No hay detalle de productos.</p>
            )}

            <div className='accountOrderActions'>
                <button type='button' className='reorderBtn' onClick={() => onReorder && onReorder(pedido)}>Comprar de nuevo</button>
                <button type='button' className='hideOrderBtn' onClick={() => onHide && onHide(pedido)}>Ocultar</button>
            </div>
        </div>
    );
}


export default function CustomerAccount({ onAuthChange }) {
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState('login');
    const [cliente, setCliente] = useState(null);
    const [login, setLogin] = useState({ email: '', password: '' });
    const [register, setRegister] = useState(emptyRegister);
    const [registerPrefix, setRegisterPrefix] = useState('+53');
    const [orders, setOrders] = useState({ pendientes: [], completados: [] });
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('pendientes');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [registerInfo, setRegisterInfo] = useState('');
    const [notifCount, setNotifCount] = useState(0);
    const [showLoginPassword, setShowLoginPassword] = useState(false);
    const [showRegisterPassword, setShowRegisterPassword] = useState(false);
    const [showRegisterPasswordConfirm, setShowRegisterPasswordConfirm] = useState(false);
    const [recoverEmail, setRecoverEmail] = useState('');

    const saveCustomer = (customer) => {
        setCliente(customer || null);
        if (customer) safeSet('ky13_customer_profile', JSON.stringify(customer));
        else safeRemove('ky13_customer_profile');
        if (onAuthChange) onAuthChange(customer || null);
        try { window.dispatchEvent(new CustomEvent('ky13CustomerUpdated', { detail: customer || null })); }
        catch(e) { const ev=document.createEvent('CustomEvent'); ev.initCustomEvent('ky13CustomerUpdated', false, false, customer || null); window.dispatchEvent(ev); }
    };

    const loadOrders = async () => {
        if (!cliente) return;
        setOrdersLoading(true);
        try {
            const res = await fetch(`${baseURL}/cliente_pedidos.php`, { credentials: 'include' });
            const data = await res.json();
            if (data?.ok) {
                setOrders(data.pedidos || { pendientes: [], completados: [] });
                setNotifCount(Number(data.unreadCount || 0));
            }
        } catch (e) {
            console.error('Error cargando historial:', e);
        } finally {
            setOrdersLoading(false);
        }
    };

    const loadMe = async () => {
        try {
            const res = await fetch(`${baseURL}/cliente_me.php`, { credentials: 'include' });
            const data = await res.json();
            if (data?.ok) {
                saveCustomer(data.cliente || null);
            }
        } catch (e) {
            const local = safeGet('ky13_customer_profile');
            if (local) {
                try { setCliente(JSON.parse(local)); } catch (_) {}
            }
        }
    };

    useEffect(() => {
        loadMe();
    }, []);

    useEffect(() => {
        if (open && cliente) {
            loadOrders();
            fetch(`${baseURL}/cliente_notificaciones_read.php`, { method:'POST', credentials:'include' }).then(()=>setNotifCount(0)).catch(()=>{});
        }
    }, [open, cliente]);

    useEffect(() => {
        if (!cliente) return;
        const tick = () => {
            fetch(`${baseURL}/cliente_pedidos.php`, { credentials:'include' })
                .then(r=>r.json()).then(d=>{ if(d?.ok) setNotifCount(Number(d.unreadCount || 0)); })
                .catch(()=>{});
        };
        tick();
        const id = setInterval(tick, 25000);
        return () => clearInterval(id);
    }, [cliente]);


    const ocultarPedido = async (pedido) => {
        if (!window.confirm('¿Ocultar esta orden de tu historial?')) return;
        const fd = new FormData();
        fd.append('idPedido', pedido.idPedido);
        try {
            const r = await fetch(`${baseURL}/cliente_ocultar_pedido.php`, { method:'POST', body:fd, credentials:'include' });
            const d = await r.json();
            if (d?.ok) loadOrders();
        } catch(e) {}
    };

    const comprarDeNuevo = (pedido) => {
        try {
            const cart = (pedido.items || [])
                .map(it => ({ idProducto: Number(it.idProducto || it.idProductoRef || 0), cantidad: Number(it.cantidad || 1), item: [] }))
                .filter(x => x.idProducto > 0);
            if (cart.length) {
                safeSet('cart', JSON.stringify(cart));
                if (typeof window !== 'undefined') window.dispatchEvent(new Event('cartUpdated'));
                alert('Productos agregados al carrito.');
            } else {
                alert('Esta orden antigua no tiene productos vinculados para recompra automática.');
            }
        } catch(e) {}
    };

    const enableBrowserNotifications = async () => {
        if (typeof window === 'undefined' || !('Notification' in window)) {
            setError('Este navegador no soporta notificaciones');
            return;
        }
        const perm = await window.Notification.requestPermission();
        if (perm === 'granted') {
            try {
                await fetch(`${baseURL}/cliente_push_optin.php`, {
                    method: 'POST',
                    headers: {'Content-Type':'application/json'},
                    credentials: 'include',
                    body: JSON.stringify({enabled:true})
                });
                new Notification('Mercadito KY-13', { body: 'Notificaciones activadas correctamente.' });
            } catch(e) {}
        }
    };

    useEffect(() => {
        if (!cliente) return;
        const pollPush = async () => {
            try {
                const r = await fetch(`${baseURL}/cliente_push_check.php`, { credentials:'include' });
                const d = await r.json();
                if (d?.ok && Array.isArray(d.events) && d.events.length) {
                    d.events.forEach(ev => {
                        if ('Notification' in window && Notification.permission === 'granted') {
                            new Notification(ev.titulo || 'Mercadito KY-13', { body: ev.cuerpo || '' });
                        }
                    });
                    setNotifCount(c => c + d.events.length);
                }
            } catch(e) {}
        };
        pollPush();
        const id = setInterval(pollPush, 30000);
        return () => clearInterval(id);
    }, [cliente]);


    const submitLogin = async (e) => {
        e.preventDefault();
        setError('');
        setRegisterInfo('');
        setLoading(true);
        try {
            const res = await fetch(`${baseURL}/cliente_login.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(login)
            });
            const data = await res.json();
            if (!data.ok) {
                setError(data.error || 'No se pudo iniciar sesión');
                return;
            }
            saveCustomer(data.cliente);
            setLogin({ email: '', password: '' });
            setActiveTab('pendientes');
        } catch (e) {
            setError('Error de conexión');
        } finally {
            setLoading(false);
        }
    };

    const submitRegister = async (e) => {
        e.preventDefault();
        setError('');
        if (register.password !== register.passwordConfirm) {
            setError('Las contraseñas no coinciden');
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(`${baseURL}/cliente_register.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ ...register, telefono: normalizePhone(registerPrefix, register.telefono) })
            });
            const data = await res.json();
            if (!data.ok) {
                setError(data.error || 'No se pudo crear la cuenta');
                return;
            }
            if (data.requiresVerification) {
                setRegisterInfo(data.message || 'Te enviamos un correo de verificación. Revisa tu email para activar tu cuenta.');
                setRegister(emptyRegister);
                setRegisterPrefix('+53');
                setMode('login');
                return;
            }
            saveCustomer(data.cliente);
            setRegister(emptyRegister);
            setRegisterPrefix('+53');
            setActiveTab('pendientes');
        } catch (e) {
            setError('Error de conexión');
        } finally {
            setLoading(false);
        }
    };


    const submitRecover = async (e) => {
        e.preventDefault();
        setError('');
        setRegisterInfo('');
        setLoading(true);
        try {
            const res = await fetch(`${baseURL}/cliente_password_request.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ email: recoverEmail })
            });
            const data = await res.json();
            if (!data.ok) {
                setError(data.error || 'No se pudo enviar el correo');
                return;
            }
            setRegisterInfo(data.message || 'Te enviamos un correo para restablecer tu contraseña.');
            setRecoverEmail('');
            setMode('login');
        } catch (e) {
            setError('Error de conexión');
        } finally {
            setLoading(false);
        }
    };

    const logout = async () => {
        try {
            await fetch(`${baseURL}/cliente_logout.php`, { method: 'POST', credentials: 'include' });
        } catch (e) {}
        saveCustomer(null);
        setOrders({ pendientes: [], completados: [] });
        setOpen(false);
    };

    const currentOrders = activeTab === 'pendientes' ? orders.pendientes : orders.completados;

    return (
        <>
            <button className='customerAccountBtn' onClick={() => setOpen(true)} type='button' title={cliente ? 'Mi cuenta' : 'Crear cuenta / entrar'}>
                {cliente ? <span className='customerInitial'>{(cliente.nombreCompleto || cliente.email || 'U').charAt(0).toUpperCase()}</span> : <span className='customerIconDraw' aria-hidden='true'></span>}
                {notifCount > 0 ? <span className='customerNotifBadge'>{notifCount}</span> : null}
            </button>

            <Modal isOpen={open} onRequestClose={() => setOpen(false)} className='customerModal customerModalWide' overlayClassName='customerOverlay'>
                <div className='customerHead'>
                    <div>
                        <strong>{cliente ? 'Mi cuenta y pedidos' : 'Cuenta KY-13'}</strong>
                        <p>{cliente ? 'Consulta tus datos y el estado de tus órdenes.' : 'Entra o crea una cuenta para comprar más rápido.'}</p>
                    </div>
                    <button onClick={() => setOpen(false)} type='button'>×</button>
                </div>

                {cliente ? (
                    <div className='customerDashboard'>
                        <section className='customerInfoPanel'>
                            <div className='customerMiniAvatar'>{(cliente.nombreCompleto || 'C')[0]?.toUpperCase()}</div>
                            <div>
                                <h3>{cliente.nombreCompleto}</h3>
                                <p>{cliente.telefono}</p><p>{cliente.email}</p>
                                <p>{cliente.email}</p>
                            </div>
                            <div className='customerAddressBox'>
                                <span>Dirección principal</span>
                                <strong>{cliente.direccion}</strong>
                            </div>
                            <button className='customerPrimary customerDanger' onClick={logout} type='button'>Cerrar sesión</button>
                        </section>

                        <section className='customerOrdersPanel'>
                            <div className='ordersHeader'>
                                <div>
                                    <strong>Historial de pedidos</strong>
                                    <p>Revisa pedidos pendientes y completados.</p>
                                </div>
                                <button type='button' onClick={loadOrders} className='refreshOrdersBtn'>Actualizar</button>
                            </div>

                            <div className='ordersTabs'>
                                <button type='button' className={activeTab === 'pendientes' ? 'active' : ''} onClick={() => setActiveTab('pendientes')}>
                                    Pendientes <span>{orders.pendientes?.length || 0}</span>
                                </button>
                                <button type='button' className={activeTab === 'completados' ? 'active' : ''} onClick={() => setActiveTab('completados')}>
                                    Completados <span>{orders.completados?.length || 0}</span>
                                </button>
                            </div>

                            <div className='ordersList'>
                                {ordersLoading ? (
                                    <p className='emptyOrders'>Cargando pedidos...</p>
                                ) : currentOrders?.length ? (
                                    currentOrders.map((pedido) => <PedidoCard key={pedido.idPedido} pedido={pedido} onReorder={comprarDeNuevo} onHide={ocultarPedido} />)
                                ) : (
                                    <p className='emptyOrders'>No tienes pedidos en esta sección.</p>
                                )}
                            </div>
                        </section>
                    </div>
                ) : (
                    <>
                        <div className='customerTabs'>
                            <button type='button' className={mode === 'login' ? 'active' : ''} onClick={() => setMode('login')}>Entrar</button>
                            <button type='button' className={mode === 'register' ? 'active' : ''} onClick={() => setMode('register')}>Crear cuenta</button>
                        </div>

                        {error && <div className='customerError'>{error}</div>}

                        {mode === 'recover' ? (
                            <form className='customerForm' onSubmit={submitRecover}>
                                <p className='recoverIntro'>Escribe tu correo y te enviaremos un enlace para restablecer tu contraseña.</p>
                                <input type='email' required placeholder='Correo' value={recoverEmail} onChange={(e) => setRecoverEmail(e.target.value)} />
                                <button className='customerPrimary' disabled={loading}>{loading ? 'Enviando...' : 'Enviar enlace'}</button>
                                <button type='button' className='forgotPasswordBtn' onClick={() => setMode('login')}>Volver al inicio de sesión</button>
                            </form>
                        ) : mode === 'login' ? (
                            <form className='customerForm' onSubmit={submitLogin}>
                                <input type='email' required placeholder='Correo' value={login.email} onChange={(e) => setLogin({ ...login, email: e.target.value })} />
                                <div className='passwordField'>
                                    <input type={showLoginPassword ? 'text' : 'password'} required placeholder='Contraseña' value={login.password} onChange={(e) => setLogin({ ...login, password: e.target.value })} />
                                    <button type='button' onClick={() => setShowLoginPassword(!showLoginPassword)}>{showLoginPassword ? '🙈' : '👁️'}</button>
                                </div>
                                <button className='customerPrimary' disabled={loading}>{loading ? 'Entrando...' : 'Entrar'}</button>
                                <button type='button' className='forgotPasswordBtn' onClick={() => setMode('recover')}>¿Olvidaste tu contraseña?</button>
                            </form>
                        ) : (
                            <form className='customerForm' onSubmit={submitRegister}>
                                <input required placeholder='Nombre completo' value={register.nombreCompleto} onChange={(e) => setRegister({ ...register, nombreCompleto: e.target.value })} />
                                <input required placeholder='Dirección' value={register.direccion} onChange={(e) => setRegister({ ...register, direccion: e.target.value })} />
                                <div className='phonePrefixGroup'>
                                    <select value={registerPrefix} onChange={(e) => setRegisterPrefix(e.target.value)} aria-label='Prefijo del país'>
                                        {COUNTRY_PREFIXES.map((p) => (
                                            <option key={p.code + p.dial} value={p.dial}>{p.flag} {p.name} {p.dial}</option>
                                        ))}
                                    </select>
                                    <input required placeholder='Teléfono' value={register.telefono} onChange={(e) => setRegister({ ...register, telefono: e.target.value })} />
                                </div>
                                <input type='email' required placeholder='Correo' value={register.email} onChange={(e) => setRegister({ ...register, email: e.target.value })} />
                                <div className='passwordField'>
                                    <input type={showRegisterPassword ? 'text' : 'password'} required minLength='6' placeholder='Contraseña' value={register.password} onChange={(e) => setRegister({ ...register, password: e.target.value })} />
                                    <button type='button' onClick={() => setShowRegisterPassword(!showRegisterPassword)}>{showRegisterPassword ? '🙈' : '👁️'}</button>
                                </div>
                                <div className='passwordField'>
                                    <input type={showRegisterPasswordConfirm ? 'text' : 'password'} required minLength='6' placeholder='Verificar contraseña' value={register.passwordConfirm} onChange={(e) => setRegister({ ...register, passwordConfirm: e.target.value })} />
                                    <button type='button' onClick={() => setShowRegisterPasswordConfirm(!showRegisterPasswordConfirm)}>{showRegisterPasswordConfirm ? '🙈' : '👁️'}</button>
                                </div>
                                <button className='customerPrimary' disabled={loading}>{loading ? 'Creando...' : 'Crear cuenta'}</button>
                            </form>
                        )}
                    </>
                )}
            </Modal>
        </>
    );
}
